import React, {Fragment, useContext, useEffect, useState} from 'react';
import colorVariables from '@densityco/ui/variables/colors.json';
import fontVariables from '@densityco/ui/variables/fonts.json';
import colorAlpha from 'color-alpha';

import {ConfigStatus, ConfigType} from '../../types/sensor-configuration';

import {CaptureViewerProps, CaptureViewerVideoContext} from '.';

import DragHandle from './drag-handle';
import {fps30} from '../../utils/constants';

function getAdjustableLineColor(props: CaptureViewerProps) {
  const alpha = props.showPoint ? 0.5 : 1;
  switch (props.configState.config_status) {
    case ConfigStatus.EXISTING:
      return colorAlpha(colorVariables.red, alpha);
    case ConfigStatus.MODIFIED:
      return colorAlpha(colorVariables.yellow, alpha);
    case ConfigStatus.NEW:
      return colorAlpha(colorVariables.green, alpha);
  }
}

function getPointAlongBezierCurve(
    [_direction, startPoint, controlPoint, endPoint],
    t: number,
): [number, number] {
  const x =
      (1 - t) * (1 - t) * startPoint[0] +
      2 * (1 - t) * t * controlPoint[0] +
      t * t * endPoint[0];
  const y =
      (1 - t) * (1 - t) * startPoint[1] +
      2 * (1 - t) * t * controlPoint[1] +
      t * t * endPoint[1];
  return [x, y];
}

type Overlay = {
  isVisible: (props: CaptureViewerProps) => boolean;
  render: React.FunctionComponent<CaptureViewerProps>;
};

const TRIGGER_LINE_OVERLAY: Overlay = {
  isVisible: props =>
    (props.configState.mutable_config_template ===
      ConfigType.TRIGGER_LINE_BOTTOM ||
      props.configState.mutable_config_template ===
        ConfigType.TRIGGER_LINE_TOP) &&
    props.configState.mutable_config.trigger_line &&
    typeof props.configState.mutable_config.trigger_line
      .x0_position_override === 'number' &&
    typeof props.configState.mutable_config.trigger_line
      .x1_position_override === 'number' &&
    typeof props.configState.mutable_config.trigger_line
      .y0_position_override === 'number' &&
    typeof props.configState.mutable_config.trigger_line
      .y1_position_override === 'number',
  render: props => {
    const [triggerLine, setTriggerLine] = useState(
      props.configState.mutable_config.trigger_line,
    );

    // When the singularity updates externally, update the internally cached value.
    useEffect(() => {
      setTriggerLine(props.configState.mutable_config.trigger_line);
    }, [
      props.configState.mutable_config.trigger_line.x0_position_override,
      props.configState.mutable_config.trigger_line.x1_position_override,
      props.configState.mutable_config.trigger_line.y0_position_override,
      props.configState.mutable_config.trigger_line.y1_position_override,
      props.configState.mutable_config.trigger_line.upper_bound_override,
      props.configState.mutable_config.trigger_line.lower_bound_override,
    ]);

    const x0 = triggerLine.x0_position_override,
        y0 = triggerLine.y0_position_override,
        x1 = triggerLine.x1_position_override,
        y1 = triggerLine.y1_position_override;

    // The length of the trigger line
    const d = Math.sqrt(Math.pow(x0 - x1, 2) + Math.pow(y0 - y1, 2));

    const lineColor = getAdjustableLineColor(props);

    return (
        <g id="triggerline">
          {/* Center line */}
          <line x1={x0} y1={y0} x2={x1} y2={y1} stroke={lineColor} strokeWidth={1.5}/>

          {/* Top / Bottom lines */}
          {[1, -1].map(sign => {
            const override =
                sign === -1
                    ? triggerLine.lower_bound_override
                    : triggerLine.upper_bound_override;

            const x0p = x0 + (sign * override * (y1 - y0)) / d,
                x1p = x1 + (sign * override * (y1 - y0)) / d,
                y0p = y0 + (sign * override * (x0 - x1)) / d,
                y1p = y1 + (sign * override * (x0 - x1)) / d;

            return (
                <line
                    key={sign}
                    x1={x0p}
                    y1={y0p}
                    x2={x1p}
                    y2={y1p}
                    stroke={lineColor}
                    strokeWidth={0.5}
                />
            );
          })}

          {/* Drag handles */}
          {!props.readOnly ? (
              <Fragment>
                {/* Left */}
                <DragHandle
                    testId="left"
                    x={x0}
                    y={y0}
                    onChange={(x, y) => {
                      setTriggerLine({
                        ...triggerLine,
                        x0_position_override: x,
                        y0_position_override: y,
                      });
                    }}
                    onMouseUp={(x, y) => {
                      props.onConfigChange({
                        ...props.configState.mutable_config,
                        trigger_line: {
                          ...props.configState.mutable_config.trigger_line,
                          x0_position_override: x,
                          y0_position_override: y,
                        },
                      });
                    }}
                />

                {/* Right */}
                <DragHandle
                    testId="right"
                    x={x1}
                    y={y1}
                    onChange={(x, y) => {
                      setTriggerLine({
                        ...triggerLine,
                        x1_position_override: x,
                        y1_position_override: y,
                      });
                    }}
                    onMouseUp={(x, y) => {
                      props.onConfigChange({
                        ...props.configState.mutable_config,
                        trigger_line: {
                          ...props.configState.mutable_config.trigger_line,
                          x1_position_override: x,
                          y1_position_override: y,
                        },
                      });
                    }}
                />
              </Fragment>
          ) : null}
        </g>
    );
  },
};

const POLYGONAL_REGION_OVERLAY: Overlay = {
  isVisible: props =>
    props.configState.mutable_config_template ===
      ConfigType.SINGULARITY_POLYGONAL &&
    props.configState.mutable_config.singularity &&
    props.configState.mutable_config.singularity.region &&
    Array.isArray(props.configState.mutable_config.singularity.region),

  render: props => {
    const [singularity, setSingularity] = useState(
        props.configState.mutable_config.singularity,
    );

    // When the singularity updates externally, update the internally cached value.
    useEffect(() => {
      setSingularity(props.configState.mutable_config.singularity);
    }, [ props.configState.mutable_config.singularity.region ]);

    const lineColor = getAdjustableLineColor(props);

    return (
        <g id="polygonal">
          {/* Polygonal region path */}
          <path
              d={singularity.region.reduce((path, point) => {
                if (path) {
                  return `${path} L${point[0]},${point[1]}`;
                } else {
                  return `M${point[0]},${point[1]}`;
                }
              }, null) + `L${singularity.region[0]},${singularity.region[1]}`}
              stroke={lineColor}
              fill="transparent"
          />

          {/* Drag handles */}
          {!props.readOnly
              ? singularity.region.map((point, index) => {
                return (
                    <DragHandle
                        testId={`point-${index}`}
                        key={point.join(',')}
                        x={point[0]}
                        y={point[1]}
                        onChange={(x, y) => {
                          const newRegion = singularity.region.slice();
                          newRegion[index] = [x, y];
                          setSingularity({...singularity, region: newRegion});
                        }}
                        onMouseUp={(x, y) => {
                          const newRegion = singularity.region.slice();
                          newRegion[index] = [x, y];

                          props.onConfigChange({
                            ...props.configState.mutable_config,
                            singularity: {
                              ...props.configState.mutable_config.singularity,
                              region: newRegion,
                            },
                          });
                        }}
                    />
                );
              }, null)
              : null}
        </g>
    );
  },
};

const SINGULARITY_REGION_OVERLAY: Overlay = {
  isVisible: props =>
    [
      ConfigType.SINGULARITY_BOTTOM,
      ConfigType.SINGULARITY_WALL_LEFT,
      ConfigType.SINGULARITY_WALL_RIGHT,
    ].includes(props.configState.mutable_config_template) &&
    props.configState.mutable_config.singularity &&
    props.configState.mutable_config.singularity.sensei_expression &&
    Array.isArray(props.configState.mutable_config.singularity.region),

  render: props => {
    // Cache the sensei expression inside.
    // On mouse up, call the prop to inform the parent component.

    const [senseiExpression, setSenseiExpression] = useState(
        JSON.parse(
            props.configState.mutable_config.singularity.sensei_expression,
        ),
    );

    // When the sensei expression updates externally, update the internally cached value.
    useEffect(() => {
      setSenseiExpression(
          JSON.parse(
              props.configState.mutable_config.singularity.sensei_expression,
          ),
      );
    }, [ props.configState.mutable_config.singularity.sensei_expression ]);

    const [direction, startPoint, controlPoint, endPoint] = senseiExpression;

    // After the drag interaction has completed, rasterize the quadratic curve into a set of points.
    const finishDragging = expression => {
      const sampleCount = 10;

      let curveSamples = [];
      for (let i = 0; i <= sampleCount; i++) {
        let t = i / sampleCount;
        let [x, y] = getPointAlongBezierCurve(expression, t);
        curveSamples.unshift([x, y]);
      }

      // Calculate the singularity region from the sensei expression
      let region;
      switch (props.configState.mutable_config_template) {
        case ConfigType.SINGULARITY_BOTTOM: {
          let [xa0, ya0] = getPointAlongBezierCurve(expression, 0);
          let [xa1, ya1] = [xa0, 120];

          let [xb1, yb1] = getPointAlongBezierCurve(expression, 1);
          let [xb2, yb2] = [xb1, 120];
          region = [
            [xb2, yb2],
            ...curveSamples,
            [xa1, ya1],
          ];
          break;
        }

        case ConfigType.SINGULARITY_WALL_LEFT:
        case ConfigType.SINGULARITY_WALL_RIGHT: {
          const xStop = props.configState.mutable_config_template === ConfigType.SINGULARITY_WALL_LEFT ? 0 : 160;
          let [xa0, ya0] = getPointAlongBezierCurve(expression, 0);
          let [xb1, yb1] = getPointAlongBezierCurve(expression, 1);

          region = [[xStop, yb1], ...curveSamples, [xStop, ya0]];
          break;
        }
      }

      props.onConfigChange({
        ...props.configState.mutable_config,
        singularity: {
          ...props.configState.mutable_config.singularity,
          sensei_expression: JSON.stringify(expression),
          region,
        },
      });
    };

    const updateSenseiExpression = (index, value) => {
      const expression = senseiExpression.slice();
      expression[index] = value;
      return expression;
    };

    // Render the path using the correct method depending on the config template
    let path = '';
    switch (props.configState.mutable_config_template) {
      case ConfigType.SINGULARITY_BOTTOM:
        path = `
        M${startPoint[0]},${startPoint[1]}
        Q${controlPoint[0]},${controlPoint[1]},${endPoint[0]},${endPoint[1]}
        L${endPoint[0]},120
        L${startPoint[0]},120
        L${startPoint[0]},${startPoint[1]}
      `;
        break;

      case ConfigType.SINGULARITY_WALL_LEFT:
        path = `
        M${startPoint[0]},${startPoint[1]}
        Q${controlPoint[0]},${controlPoint[1]},${endPoint[0]},${endPoint[1]}
        L0,${endPoint[1]}
        L0,${startPoint[1]}
        L${startPoint[0]},${startPoint[1]}
      `;
        break;

      case ConfigType.SINGULARITY_WALL_RIGHT:
        path = `
        M${startPoint[0]},${startPoint[1]}
        Q${controlPoint[0]},${controlPoint[1]},${endPoint[0]},${endPoint[1]}
        L160,${endPoint[1]}
        L160,${startPoint[1]}
        L${startPoint[0]},${startPoint[1]}
      `;
        break;
    }

    const lineColor = getAdjustableLineColor(props);

    return (
        <g id="singularity">
          {/* Singularity region path */}
          <path d={path} stroke={lineColor} fill="transparent"/>

          {/* To see where the raw points are, uncomment the below */}
          {/*
        {props.configState.mutable_config.singularity.region.map(
          ([x, y], i) => (
            <circle key={i} cx={x} cy={y} r={2} fill="teal" />
          ),
        )}
        <path
          d={props.configState.mutable_config.singularity.region.reduce(
            (acc, [x, y]) => `${acc} L${x},${y}`,
            `M${props.configState.mutable_config.singularity.region[0].join(',')}`
          )}
          stroke="teal"
          fill="transparent"
        />
        */}

          {/* Drag handles */}
          {!props.readOnly ? (
              <Fragment>
                {/* Start Handle */}
                <DragHandle
                    testId="start"
                    x={startPoint[0]}
                    y={startPoint[1]}
                    onChange={(x, y) =>
                        setSenseiExpression(updateSenseiExpression(1, [x, y]))
                    }
                    onMouseUp={(x, y) =>
                        finishDragging(updateSenseiExpression(1, [x, y]))
                    }
                />

                {/* Control Handle */}
                <DragHandle
                    testId="control"
                    x={controlPoint[0]}
                    y={controlPoint[1]}
                    onChange={(x, y) =>
                        setSenseiExpression(updateSenseiExpression(2, [x, y]))
                    }
                    onMouseUp={(x, y) =>
                        finishDragging(updateSenseiExpression(2, [x, y]))
                    }
                />

                {/* End Handle */}
                <DragHandle
                    testId="end"
                    x={endPoint[0]}
                    y={endPoint[1]}
                    onChange={(x, y) =>
                        setSenseiExpression(updateSenseiExpression(3, [x, y]))
                    }
                    onMouseUp={(x, y) =>
                        finishDragging(updateSenseiExpression(3, [x, y]))
                    }
                />
              </Fragment>
          ) : null}
        </g>
    );
  },
};

const MPI_OVERLAPPING_REGION_OVERLAY: Overlay = {
  isVisible: props => typeof props.mpiOverlappingRegionOverlay !== 'undefined',

  render: ({showPoint, mpiOverlappingRegionOverlay}) => {
    const overlappingRegionX = (mpiOverlappingRegionOverlay.side === 'right'
                  ? 160 - mpiOverlappingRegionOverlay.width
                  : mpiOverlappingRegionOverlay.width) - 0.5

    const overlappingRegionWithMultiplierWidth = mpiOverlappingRegionOverlay.width * mpiOverlappingRegionOverlay.maxXOffsetMultiplier;
    const overlappingRegionWithMultiplierX = (mpiOverlappingRegionOverlay.side === 'right'
                  ? 160 - overlappingRegionWithMultiplierWidth
                  : overlappingRegionWithMultiplierWidth) - 0.5

    return (
      <g id="mpi-overlapping-region">
        {/*
        The green region represents the effective area that deduplication occurs in.
        This takes into account the `max_x_offset_multiplier` value.
        */}
        <rect
          x={
            mpiOverlappingRegionOverlay.side === 'right'
              ? 160 - overlappingRegionWithMultiplierWidth
              : 0
          }
          y={0}
          width={overlappingRegionWithMultiplierWidth}
          height={120}
          fill={`rgba(0, 255, 0, ${showPoint ? 0.08 : 0.15})`}
        />
        <line
          x1={overlappingRegionWithMultiplierX}
          y1={0}
          x2={overlappingRegionWithMultiplierX}
          y2={120}
          strokeWidth={1}
          stroke={`rgba(0, 255, 0, ${showPoint ? 0.1 : 0.35})`}
          shapeRendering="crispEdges"
        />

        {/* Add a set of "tick" marks representing the scale of the max x offset multipler values*/}
        {[0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1, 1.1, 1.2, 1.3, 1.4, 1.5].map(tick => {
          const tickX = mpiOverlappingRegionOverlay.width * tick;
          const tickXInPx = (mpiOverlappingRegionOverlay.side === 'right' ? 160 - tickX : tickX) - 0.5;

          // Label every tick that is a multiple of 0.5
          const tickIsLabelled = tick !== 0 && tick % 0.5 === 0;

          return (
            <g key={tick} transform={`translate(${tickXInPx}, 0)`}>
              <line
                x1={0}
                y1={0}
                x2={0}
                y2={tickIsLabelled ? 6 : 4}
                strokeWidth={0.5}
                stroke={`rgba(0, 255, 0, ${showPoint ? 0.1 : 0.75})`}
                shapeRendering="crispEdges"
              />
              {tickIsLabelled ? (
                <text
                  transform="translate(0, 7)"
                  alignmentBaseline="text-before-edge"
                  textAnchor="middle"
                  fontSize={4}
                  fontFamily={fontVariables.fontBase}
                  fill={`rgba(0, 255, 0, ${showPoint ? 0.1 : 0.75})`}
                  style={{userSelect: 'none'}}
                >{tick}</text>
              ) : null}
            </g>
          );
        })}
      </g>
    );
  },
};

const SHOW_POINT_OVERLAY: Overlay = {
  isVisible: props => typeof props.showPoint !== 'undefined',

  render: ({showPoint}) => (
      <g id="show-point">
        <line
            x1={showPoint[0]}
            y1={0}
            x2={showPoint[0]}
            y2={120}
            stroke="white"
            strokeWidth={1}
            strokeDasharray="1"
        />
        <line
            x1={0}
            y1={showPoint[1]}
            x2={160}
            y2={showPoint[1]}
            stroke="white"
            strokeWidth={1}
            strokeDasharray="1"
        />
        <circle
            cx={showPoint[0]}
            cy={showPoint[1]}
            r={2}
            fill={colorVariables.yellow}
        />
      </g>
  ),
};

const MAX_PIXEL_INTENSITY_OVERLAY_COLOR = (
    colorAlpha(colorVariables.blue, 0.6)
        .split(',')
        .map(i => parseFloat(i.replace('rgba(', '')))
);
const MAX_PIXEL_INTENSITY_OVERLAY: Overlay = {
  isVisible: props =>
    props.showMaxPixelIntensityOverlay &&
    props.configState.mutable_config.filter &&
    typeof props.configState.mutable_config.filter
      .max_pixel_intensity_override === 'number',

  render: ({configState}) => {
    const video = useContext(CaptureViewerVideoContext);
    const maxPixelIntensity = configState.mutable_config.filter.max_pixel_intensity_override;

    useEffect(() => {
      if (!video) {
        return;
      }
      const bbox = video.getBoundingClientRect();

      // Create two canvases:
      // 1. A "working" canvas to render the video frame to and read the pixel data
      // 2. A "visible" canvas to render to the screen and show the filered pixel data within.
      const working = document.createElement('canvas');
      working.width = bbox.width;
      working.height = bbox.height;
      const workingContext = working.getContext('2d');

      const visible = document.createElement('canvas');
      visible.style.position = 'absolute';
      visible.style.zIndex = '1';
      visible.width = bbox.width;
      visible.height = bbox.height;
      const visibleContext = visible.getContext('2d');

      // Add canvas to DOM right after video
      video.after(visible);

      // Run a requestanimationframe to read pixels from the video and update the canvas to
      // highlight any pixels that are saturated.
      let currentAnimationFrame: number | null = null;

      function animationFrame() {
        currentAnimationFrame = null;

        if (maxPixelIntensity < 255) {
          // Get image data from video, and render to the working canvas
          workingContext.drawImage(video, 0, 0, bbox.width, bbox.height);
          const workingImageData = workingContext.getImageData(0, 0, bbox.width, bbox.height);
          const data = workingImageData.data;

          for (let i = 0; i < data.length; i += 4) {
            if (data[i] > maxPixelIntensity) {
              data[i] = MAX_PIXEL_INTENSITY_OVERLAY_COLOR[0]; // R
              data[i + 1] = MAX_PIXEL_INTENSITY_OVERLAY_COLOR[1]; // G
              data[i + 2] = MAX_PIXEL_INTENSITY_OVERLAY_COLOR[2]; // B
              data[i + 3] = MAX_PIXEL_INTENSITY_OVERLAY_COLOR[3] * 255; // A
            } else {
              // No color here, so make it transparent
              data[i + 3] = 0; // A
            }
          }

          // Clear canvas, and draw pixel overlay
          visibleContext.fillStyle = 'rgba(0, 0, 0, 0)';
          visibleContext.fillRect(0, 0, bbox.width, bbox.height);
          visibleContext.putImageData(workingImageData, 0, 0);
        }

        // Schedule next frame
        currentAnimationFrame = window.requestAnimationFrame(animationFrame);
      }

      currentAnimationFrame = window.requestAnimationFrame(animationFrame);

      return () => {
        window.cancelAnimationFrame(currentAnimationFrame);
        visible.remove();
      };
    }, [maxPixelIntensity, video]);

    return null;
  },
};

const BOUNDING_BOXES_OVERLAY: Overlay = {
  isVisible: props => true,

  render: props => {
    const video = useContext(CaptureViewerVideoContext);
    if (!video) {
      return null;
    }

    if (!props.capture.inferences) {
      return null;
    }

    const currentTime = Math.floor(video.currentTime * fps30);
    if (!props.capture.inferences[currentTime]) {
      return null;
    }

    return <g id="bounding-boxes">
      {...props.capture.inferences[currentTime].map(inf =>
          <rect
              x={inf.bbox[0]}
              y={inf.bbox[1]}
              width={inf.bbox[2]}
              height={inf.bbox[3]}
              stroke={colorVariables.purple}
              strokeWidth={1}
              fill="transparent"
          />
      )}
      {...props.capture.inferences[currentTime].map(inf =>
          <rect
              x={inf.centroid[0] - .5}
              y={inf.centroid[1] - .5}
              width={1}
              height={1}
              stroke="rgb(171, 103, 181)"
              strokeWidth={.5}
              fill="transparent"
          />
      )}
    </g>;
  },
};


const DEFAULT_OVERLAYS = [
  MAX_PIXEL_INTENSITY_OVERLAY,
  MPI_OVERLAPPING_REGION_OVERLAY,

  TRIGGER_LINE_OVERLAY,
  POLYGONAL_REGION_OVERLAY,
  SINGULARITY_REGION_OVERLAY,

  SHOW_POINT_OVERLAY,

  BOUNDING_BOXES_OVERLAY,
];

export default DEFAULT_OVERLAYS;
